import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'

import { completeMailboxConnect } from '@/lib/api/v2/planning'
import { ApiError } from '@/lib/api/client'
import { extractAadstsCode, takeAttempt } from '@/lib/mailboxes'

/**
 * SOLY-SUPPLIER-002 MS-02/MS-04 — where Microsoft sends the browser back after the sign-in. Hands the
 * code and the one-time state to the API (which follows the mailbox typed before leaving), then
 * returns to the mailboxes page, with the API's reason when it refused. MS-05: when Microsoft did not
 * give a code, or refused it for lack of consent, the page reopens the attempt with the help for the
 * administrator (?retry=1, plus Microsoft's AADSTS code when there is one).
 */
export default function MailboxCallbackPage() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const sent = useRef(false)

  useEffect(() => {
    // StrictMode mounts twice in development; the code is single-use, so it is sent once.
    if (sent.current) return
    sent.current = true

    const back = (query: string) => navigate(`/supplier-orders/mailboxes?${query}`, { replace: true })
    const retry = (aadsts: string | null) => back(aadsts ? `retry=1&code=${encodeURIComponent(aadsts)}` : 'retry=1')
    const code = params.get('code')
    const state = params.get('state')
    if (params.get('error') || !code || !state) {
      retry(extractAadstsCode(params.get('error_description')))
      return
    }

    completeMailboxConnect(state, code)
      .then(() => {
        takeAttempt()
        back('connected=1')
      })
      .catch((e) => {
        if (e instanceof ApiError && e.code === 'MAILBOX_CONNECT_REFUSED') {
          retry(extractAadstsCode(e.message))
          return
        }
        takeAttempt()
        back(`error=${encodeURIComponent(e instanceof ApiError ? e.message : 'La connexion à Outlook a échoué.')}`)
      })
  }, [params, navigate])

  return (
    <div className="flex min-h-screen items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="mailbox-callback">
      <Loader2 className="h-4 w-4 animate-spin" />
      Connexion de la boîte…
    </div>
  )
}
